import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LOCATIONS } from "@/data/locations";

// Abstract geographic placement (viewBox 0..100). Garhwal west, Kumaon east.
const NODES = {
  dehradun: { x: 16, y: 56 },
  mussoorie: { x: 19, y: 46 },
  rishikesh: { x: 27, y: 64 },
  haridwar: { x: 31, y: 73 },
  nainital: { x: 73, y: 61 },
  mukteshwar: { x: 78, y: 52 },
  almora: { x: 82, y: 45 },
  ranikhet: { x: 73, y: 41 },
};

export const GeographicIntelligence = () => {
  const reduce = useReducedMotion();
  const [active, setActive] = useState("dehradun");
  const loc = LOCATIONS.find((l) => l.slug === active);

  return (
    <div className="grid lg:grid-cols-[1.25fr_1fr] gap-10 lg:gap-16 items-center" data-testid="geographic-intelligence">
      {/* Map */}
      <div className="relative">
        <svg viewBox="0 0 100 80" className="w-full h-auto" role="img" aria-label="Interactive map of Uttarakhand locations">
          {/* contour backdrop */}
          <g stroke="#C8C2B7" strokeWidth="0.2" fill="none" opacity="0.6">
            <path d="M4 60 C 25 52, 45 56, 60 50 S 92 44, 98 48" />
            <path d="M4 52 C 25 44, 46 48, 62 42 S 92 36, 98 40" strokeDasharray="1 1.2" />
            <path d="M6 44 C 26 37, 48 40, 64 34 S 92 29, 98 32" />
            <path d="M8 36 C 28 30, 50 32, 66 27 S 92 22, 98 25" strokeDasharray="1 1.2" />
          </g>
          {/* region divider */}
          <line x1="48" y1="20" x2="52" y2="74" stroke="#E2DDD5" strokeWidth="0.3" strokeDasharray="1 1.5" />
          <text x="22" y="18" className="font-mono-ss" fontSize="2.4" fill="#8A9098" letterSpacing="0.3">GARHWAL</text>
          <text x="66" y="18" className="font-mono-ss" fontSize="2.4" fill="#8A9098" letterSpacing="0.3">KUMAON</text>

          {LOCATIONS.map((l) => {
            const n = NODES[l.slug];
            if (!n) return null;
            const isActive = active === l.slug;
            return (
              <g
                key={l.slug}
                transform={`translate(${n.x} ${n.y})`}
                onClick={() => setActive(l.slug)}
                className="cursor-pointer"
                data-testid={`map-node-${l.slug}`}
              >
                {isActive && (
                  <motion.circle
                    initial={reduce ? false : { r: 2, opacity: 0.8 }}
                    animate={reduce ? { r: 4 } : { r: 5, opacity: 0 }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                    r="4"
                    fill="none"
                    stroke="#2D3B32"
                    strokeWidth="0.3"
                  />
                )}
                <circle r={isActive ? 1.6 : 1.1} fill={isActive ? "#2D3B32" : "#8C6A53"} className="transition-all" />
                <circle r="3.2" fill="transparent" />
                <text
                  x={n.x > 50 ? 3 : -3}
                  y="1"
                  textAnchor={n.x > 50 ? "start" : "end"}
                  fontSize="2.3"
                  className="font-body"
                  fill={isActive ? "#1A1C1E" : "#52575D"}
                  fontWeight={isActive ? 600 : 400}
                >
                  {l.name}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Detail panel */}
      <div className="relative min-h-[320px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: reduce ? 0 : 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduce ? 0 : -14 }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
          >
            <p className="font-mono-ss text-[0.7rem] tracking-widest text-[#8A9098] mb-3">
              {loc.coords} · {loc.elevation}
            </p>
            <h3 className="font-display text-4xl sm:text-5xl font-light text-[#1A1C1E] leading-none">{loc.name}</h3>
            <p className="mt-2 ss-overline text-[#8C6A53]">{loc.region}</p>
            <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-[#52575D]">{loc.tagline}</p>

            <ul className="mt-6 space-y-2">
              {loc.quickFacts.slice(0, 3).map((f) => (
                <li key={f.label} className="flex items-baseline gap-3 text-sm">
                  <span className="font-mono-ss text-[0.66rem] uppercase tracking-wider text-[#8A9098] w-28 shrink-0">{f.label}</span>
                  <span className="text-[#1A1C1E]">{f.value}</span>
                </li>
              ))}
            </ul>

            <Link
              to={`/locations/${loc.slug}`}
              className="group mt-8 inline-flex items-center gap-2 text-[0.78rem] uppercase tracking-[0.1em] text-[#1A1C1E]"
              data-testid={`map-detail-link-${loc.slug}`}
            >
              <span className="ss-link">Open {loc.name} intelligence</span>
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GeographicIntelligence;
